import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import { MaskedLines } from "@/components/motion/Reveal";
import { whatsappHref } from "@/components/util";
import { brand } from "@/data/content";
import { contactSchema } from "@/data/seo";

const CONTACT_CRUMBS = [{ name: "Contact", path: "/contact" }];
const CONTACT_SCHEMA = [contactSchema()];

const SERVICES = [
  "Brand Strategy",
  "Creative & Design",
  "Content & Social Media",
  "Digital Marketing & Growth",
  "PR & Media Relations",
  "Media Support",
  "Events, Launches & Activations",
  "Influencer Marketing",
  "Research & Consulting",
  "Not sure yet",
  "Other",
];
const BUDGETS = ["Under ₹1L / month", "₹1L – ₹3L / month", "₹3L – ₹8L / month", "₹8L+ / month", "Prefer to discuss"];
const TIMELINES = ["ASAP", "1–3 months", "3–6 months", "Just exploring"];

export default function Contact() {
  useSeo({
    title: "Contact a Marketing Agency in Gurgaon: Send Your Brief",
    description:
      "Start a project with HashTag Marketing, integrated marketing agency in Gurgaon, Delhi NCR. Share your brief for brand, digital, PR or events. We reply within a day.",
    path: "/contact",
    pageType: "ContactPage",
    breadcrumbs: CONTACT_CRUMBS,
    schema: CONTACT_SCHEMA,
  });

  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", website: "",
    services: [], budget: "", timeline: "", brief: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleService = (s) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(s)
        ? f.services.filter((x) => x !== s)
        : [...f.services, s],
    }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || (!form.email && !form.phone) || !form.brief) {
      toast.error("Please add your name, email or phone, and a short brief.");
      return;
    }
    const msg = [
      "New project brief — HashTag",
      `Name: ${form.name}`,
      form.company && `Company: ${form.company}`,
      form.email && `Email: ${form.email}`,
      form.phone && `Phone: ${form.phone}`,
      form.website && `Website: ${form.website}`,
      form.services.length && `Services: ${form.services.join(", ")}`,
      form.budget && `Monthly Budget: ${form.budget}`,
      form.timeline && `Timeline: ${form.timeline}`,
      `Brief: ${form.brief}`,
    ].filter(Boolean).join("\n");

    if (brand.whatsapp) {
      window.open(whatsappHref(msg), "_blank", "noopener,noreferrer");
      toast.success("Opening WhatsApp with your brief…");
    } else {
      toast.info("WhatsApp number pending verification. Copy your brief and use the contact details once confirmed.");
    }
  };

  const field = "w-full border-b border-line bg-transparent py-3 text-lg text-ink outline-none transition-colors placeholder:text-muted/60 focus:border-accent";

  return (
    <>
      <section className="border-b border-line pt-28 sm:pt-32 md:pt-36 lg:pt-44">
        <div className="mx-auto max-w-[1600px] px-5 pb-10 sm:pb-12 md:pb-14 lg:pb-16 sm:px-8 md:px-10 lg:px-12 xl:px-16">
          <p className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-accent">
            <span className="h-px w-10 bg-accent" /> Contact
          </p>
          <MaskedLines
            lines={["Send us", "your brief."]}
            className="font-display text-[15vw] font-extrabold leading-[0.88] tracking-tighter text-ink md:text-[8vw]"
          />
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-5 py-14 sm:py-16 md:py-20 lg:py-24 xl:py-28 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <div className="grid gap-10 sm:gap-12 lg:grid-cols-12 lg:gap-16">
          <motion.form
            data-testid="brief-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
            className="lg:col-span-7"
          >
            <div className="grid gap-6 sm:grid-cols-2 sm:gap-8">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Name*</span>
                <input data-testid="input-name" className={field} value={form.name} onChange={set("name")} placeholder="Your name" />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Company</span>
                <input data-testid="input-company" className={field} value={form.company} onChange={set("company")} placeholder="Company / brand" />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Email ID*</span>
                <input data-testid="input-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@company.com" />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Phone Number*</span>
                <input data-testid="input-phone" type="tel" className={field} value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Website</span>
                <input data-testid="input-website" className={field} value={form.website} onChange={set("website")} placeholder="https://" />
              </label>

              <fieldset className="sm:col-span-2">
                <legend className="text-xs font-bold uppercase tracking-[0.14em] text-muted">
                  What services are you looking for?{" "}
                  <span className="ml-1 text-muted/70">(Select all that apply)</span>
                </legend>
                <div
                  data-testid="services-checkboxes"
                  className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2"
                >
                  {SERVICES.map((s) => {
                    const active = form.services.includes(s);
                    const id = s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                    return (
                      <label
                        key={s}
                        htmlFor={`service-${id}`}
                        className={`group flex cursor-pointer items-center gap-3 border py-3 pl-3 pr-4 text-sm transition-colors ${
                          active
                            ? "border-accent bg-accent/10 text-ink"
                            : "border-line text-muted hover:border-ink hover:text-ink"
                        }`}
                      >
                        <input
                          id={`service-${id}`}
                          data-testid={`service-${id}`}
                          type="checkbox"
                          checked={active}
                          onChange={() => toggleService(s)}
                          className="h-4 w-4 shrink-0 accent-accent"
                        />
                        <span className="text-base">{s}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Monthly Budget</span>
                <select data-testid="select-budget" className={field} value={form.budget} onChange={set("budget")}>
                  <option value="">Select…</option>
                  {BUDGETS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Timeline</span>
                <select data-testid="select-timeline" className={field} value={form.timeline} onChange={set("timeline")}>
                  <option value="">Select…</option>
                  {TIMELINES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Project brief*</span>
                <textarea data-testid="input-brief" rows={4} className={`${field} resize-none`} value={form.brief} onChange={set("brief")} placeholder="Tell us what you're building and what success looks like." />
              </label>
            </div>
            <button
              type="submit"
              data-testid="brief-submit"
              className="group mt-10 inline-flex items-center gap-3 bg-accent px-8 py-5 text-sm font-bold uppercase tracking-[0.14em] text-bone transition-transform duration-300 hover:-translate-y-0.5"
            >
              Send Us Your Brief
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.form>

          <div className="lg:col-span-4 lg:col-start-9">
            <div className="border-t border-line pt-6">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Prefer to talk?</p>
              <p className="mt-4 max-w-sm text-lg leading-relaxed text-muted">
                Reach us directly on WhatsApp, phone or email. We usually respond within one working day.
              </p>
            </div>
            <div className="mt-8 space-y-6">
              <a
                href={`https://wa.me/${brand.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="flex items-start gap-3 transition-colors hover:text-accent"
              >
                <MessageCircle size={20} className="mt-1 text-accent" />
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.14em] text-ink">WhatsApp</p>
                  <p className="mt-1 text-lg text-muted">{brand.phone}</p>
                </div>
              </a>
              <a
                href={brand.phoneHref}
                data-testid="contact-phone"
                className="flex items-start gap-3 transition-colors hover:text-accent"
              >
                <Phone size={20} className="mt-1 text-accent" />
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.14em] text-ink">Phone</p>
                  <p className="mt-1 text-lg text-muted">{brand.phone}</p>
                </div>
              </a>
              <a
                href={`mailto:${brand.email}`}
                data-testid="contact-email"
                className="flex items-start gap-3 transition-colors hover:text-accent"
              >
                <Mail size={20} className="mt-1 text-accent" />
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.14em] text-ink">Email</p>
                  <p className="mt-1 text-lg text-muted break-all">{brand.email}</p>
                </div>
              </a>
              <div className="border-t border-line pt-6">
                <p className="text-sm font-bold uppercase tracking-[0.14em] text-ink">Studio</p>
                <p className="mt-2 max-w-sm text-base leading-relaxed text-muted">{brand.location}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
